import dayjs from 'dayjs';

/**
 * Format created at date to 'DD MMM YYYY' format
 *
 * @param date - The date string to format
 *
 * @returns Formatted date string
 *
 * @example
 * formatCreatedAt('2024-06-15T14:30:00Z'); // returns '15 Jun 2024'
 */
export function formatDisplayShortDate(date: Date): string {
  return dayjs(date).format('DD MMM YYYY • HH:mm');
}

/**
 * Format date to 'DD MMMM YYYY' format
 *
 * @param date - The date string to format
 *
 * @returns Formatted date string
 */
export function formatDisplayLongDate(date: Date | string): string {
  return dayjs(date).format('DD MMMM YYYY');
}

/**
 * Format date to 'DD-MM-YYYY' format
 *
 * @param date - The date string to format
 *
 * @returns Formatted date string
 *
 * @example
 * formatNumericDate('2024-06-15T14:30:00Z'); // returns '15-06-2024'
 */
export function formatNumericDate(date: Date | string): string {
  return dayjs(date).format('DD-MM-YYYY');
}

/**
 * Format date to 'YYYY-MM-DD' format
 *
 * @param date - The date string to format
 *
 * @returns Formatted date string
 *
 * @example
 * formatISODate('15-06-2024'); // returns '2024-06-15'
 */
export function formatISODate(date: Date | string): string {
  return dayjs(date).format('YYYY-MM-DD');
}

/**
 * Format time to 'HH:mm' format
 *
 * @param time - The time string to format
 *
 * @returns Formatted time string
 *
 * @example
 * formatTime('2024-06-15T14:30:00Z'); // returns '14:30'
 */
export function formatDisplayShortTime(time: Date | string): string {
  return dayjs(time).format('HH:mm');
}

/**
 * Format time to 'HH:mm:ss' format
 *
 * @param time - The time string to format
 *
 * @returns Formatted time string
 *
 * @example
 * formatTime('2024-06-15T14:30:01Z'); // returns '14:30:01'
 */
export function formatDisplayLongTime(time: Date | string): string {
  return dayjs(time).format('HH:mm:ss');
}

/**
 * Convert Unix timestamp to JavaScript Date object
 *
 * @param unixTimestamp - The Unix timestamp to convert
 *
 * @returns JavaScript Date object representing the given Unix timestamp
 *
 * @example
 * convertUnixTimestampToDate(1710000000); // returns Date object for '2024-06-15T14:30:00Z'
 */
export function convertUnixTimestampToDate(unixTimestamp: number): Date {
  return dayjs.unix(unixTimestamp).toDate();
}

/**
 * Format date and time to 'DD MMM YYYY / HH:mm' format with a custom separator
 *
 * @param dateTime - The date and time string to format
 * @param separator - The separator string to use between date and
 *                    time (default is '/')
 *
 * @returns Formatted date and time string
 *
 * @example
 * formatDisplayDateTimeWithSeparator('2024-06-15T14:30:00Z', { separator: ' - ' }); // returns '15 Jun 2024 - 14:30'
 */
export function formatDisplayDateTimeWithSeparator(
  dateTime: Date | string,
  { separator = '/' }: { separator: string }
): string {
  return dayjs(dateTime).format(`DD MMM YYYY${separator}HH:mm`);
}

export function formatISODateTimeWithoutSymbol(
  dateTime: Date | string
): string {
  return dayjs(dateTime).format('YYYYMMDDTHHmmss');
}
